import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import axios from "axios";
import { useReviewStats } from "@/hooks/useReviewStats";

const API = process.env.REACT_APP_BACKEND_URL;

// Curated fallback — used when the backend sync hasn't populated reviews yet
// (e.g., preview / fresh install). Keeps the section from ever looking broken.
const FALLBACK_REVIEWS = [
  { author_name: "Samantha R.", rating: 5, relative_time: "6 days ago", text: "Very knowledgeable, detail-oriented & cleaned up after themselves. Patient, didn't rush you through anything. Would 100% recommend to friends and family." },
  { author_name: "Tyler M.", rating: 5, relative_time: "8 days ago", text: "The best experience! We have a butcher block that needed trimming; just the idea that they were willing to pick it up was impressive — and the finished work was flawless." },
  { author_name: "dGmail", rating: 5, relative_time: "12 days ago", text: "2 people came as scheduled & did a great job. They covered a couple furniture pieces, moved them to the garage, hand-sanded and painted the ceiling. Very careful." },
  { author_name: "Jennifer L.", rating: 5, relative_time: "2 weeks ago", text: "Fast, reliable, and reasonably priced. They fixed multiple things around my house in one visit. Highly recommend!" },
  { author_name: "David K.", rating: 5, relative_time: "3 weeks ago", text: "Had water damage to my drywall and they patched it perfectly — you can't even tell where the repair was. Great craftsmanship." },
  { author_name: "Robert T.", rating: 5, relative_time: "1 month ago", text: "Fixed a stubborn leak that two other plumbers couldn't solve. Honest pricing and they cleaned up after themselves." },
];

const GOOGLE_REVIEWS_URL = "http://www.google.com/maps?cid=14793989263965381340";

const initialsOf = (name) => (name || "?").split(/\s+/).slice(0, 2).map(p => p[0]).join("").toUpperCase();

export function GoogleReviewsCarousel() {
  const [reviews, setReviews] = useState(null);
  const { ratingLabel, totalPlusLabel } = useReviewStats();

  useEffect(() => {
    let cancelled = false;
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/api/google-reviews/public`, { timeout: 8000 });
        if (cancelled) return;
        const list = (res.data?.reviews || [])
          .filter(r => r.rating >= 5 && (r.text || "").trim().length > 40)
          .slice(0, 12);
        setReviews(list.length ? list : FALLBACK_REVIEWS);
      } catch {
        if (!cancelled) setReviews(FALLBACK_REVIEWS);
      }
    };
    fetchReviews();
    return () => { cancelled = true; };
  }, []);

  const list = reviews || FALLBACK_REVIEWS;

  return (
    <section className="py-14 sm:py-16 bg-white" data-testid="google-reviews-carousel">
      <div className="max-w-7xl mx-auto px-4 mb-8 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">What Our Customers Say</h2>
        <div className="flex items-center justify-center gap-1 mb-2">
          {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />)}
        </div>
        <p className="text-gray-500 text-sm">
          <span className="font-semibold text-gray-700">{ratingLabel}</span> average from {totalPlusLabel} verified Google reviews
        </p>
      </div>

      {/* Horizontal scroll carousel — native scroll-snap, works everywhere including iOS Safari */}
      <div className="relative">
        <div
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-4 pb-4 sm:px-8"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
        >
          {list.map((review, idx) => (
            <a
              key={idx}
              href={review.profile_url || GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="snap-start flex-shrink-0 w-[85vw] sm:w-[380px] bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-orange-200 transition-all block"
              data-testid={`google-review-${idx}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {initialsOf(review.author_name)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{review.author_name}</p>
                    <p className="text-xs text-gray-500">{review.relative_time || "recently"}</p>
                  </div>
                </div>
                {/* Google G badge */}
                <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-label="Google review" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div className="flex mb-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`w-4 h-4 ${i <= (review.rating || 5) ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`} />
                ))}
              </div>
              <p className="text-gray-700 text-sm leading-relaxed line-clamp-6">{review.text}</p>
            </a>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-6 text-center">
        <a
          href="/reviews"
          className="inline-flex items-center gap-2 text-sm font-medium text-orange-700 hover:text-orange-800 transition-colors"
          data-testid="see-all-google-reviews"
        >
          See all {totalPlusLabel} reviews <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Hide the scrollbar cross-browser without breaking touch scrolling */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}

export default GoogleReviewsCarousel;
